import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { rowAction } from './rowActions';
import { useMediaQuery } from '../../lib/useMediaQuery';
import RefImageButton from './RefImageButton';
import type { Editor } from '@tiptap/react';

interface Props {
  editor: Editor | null;
  commentsOpen?: boolean;
}

export function Toolbar({ editor, commentsOpen = false }: Props) {
  const desktop = useMediaQuery('(min-width: 768px)');
  const wide = useMediaQuery('(min-width: 1600px)');
  const compact = !wide || commentsOpen;
  const [expanded, setExpanded] = useState(false);
  const [, rerender] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, width: 0, bottom: 20 });

  // Tiptap v3 does not rerender its React parent for every transaction.
  useEffect(() => {
    if (!editor) return;
    const update = () => rerender(value => value + 1);
    editor.on('transaction', update);
    return () => { editor.off('transaction', update); };
  }, [editor]);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const dock = dockRef.current;
    const column = anchor?.closest<HTMLElement>('.editor-script-column');
    if (!editor || !anchor || !dock || !column) return;
    const nav = document.querySelector<HTMLElement>('.mobile-editor-bottom');
    const view = editor.view;
    const originalMargin = view.props.scrollMargin;
    const originalThreshold = view.props.scrollThreshold;
    let lastClearance = -1;
    const measure = () => {
      if (editor.isDestroyed) return;
      const bounds = anchor.getBoundingClientRect();
      const keyboard = desktop ? 0 : Math.max(0,
        window.innerHeight - (window.visualViewport?.height ?? window.innerHeight)
          - (window.visualViewport?.offsetTop ?? 0));
      const bottom = desktop ? 20 : (nav?.getBoundingClientRect().height ?? 64) + 12 + keyboard;
      setPosition(previous => previous.left === bounds.left && previous.width === bounds.width && previous.bottom === bottom
        ? previous : { left: bounds.left, width: bounds.width, bottom });
      const space = dock.getBoundingClientRect().height + bottom + 16;
      column.style.setProperty('--editor-dock-space', `${space}px`);
      // ProseMirror already uses visualViewport.height when a keyboard opens.
      const clearance = space - keyboard;
      if (clearance !== lastClearance) {
        lastClearance = clearance;
        // Command-triggered caret scrolling must also clear the fixed dock.
        const sides = (value: typeof originalMargin, fallback: number) => typeof value === 'number'
          ? { top: value, right: value, bottom: value, left: value }
          : (value ?? { top: fallback, right: fallback, bottom: fallback, left: fallback });
        const margin = sides(originalMargin, 5);
        const threshold = sides(originalThreshold, 0);
        view.setProps({
          scrollMargin: { ...margin, bottom: Math.max(margin.bottom, clearance) },
          scrollThreshold: { ...threshold, bottom: Math.max(threshold.bottom, clearance) },
        });
      }
    };
    const observer = new ResizeObserver(measure);
    observer.observe(anchor);
    observer.observe(dock);
    if (nav) observer.observe(nav);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
      column.style.removeProperty('--editor-dock-space');
      if (!editor.isDestroyed) view.setProps({ scrollMargin: originalMargin, scrollThreshold: originalThreshold });
    };
  }, [desktop, editor]);

  useEffect(() => {
    setExpanded(false);
  }, [desktop]);

  useEffect(() => {
    if (!expanded) return;
    const dismiss = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) setExpanded(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [expanded]);

  const rowButton = (action: 'duplicate' | 'up' | 'down') => <ToolButton
    key={action}
    label={action === 'duplicate' ? (desktop ? 'Duplicate' : 'Dup') : (desktop ? `${action === 'up' ? '↑' : '↓'} Move` : action === 'up' ? '↑' : '↓')}
    ariaLabel={action === 'duplicate' ? 'Duplicate row' : `Move row ${action}`}
    disabled={!editor || !rowAction(editor.state, action)}
    onClick={() => {
      if (!editor) return;
      const tr = rowAction(editor.state, action);
      if (tr) { editor.view.dispatch(tr.scrollIntoView()); editor.commands.focus(); }
    }}
  />;
  const rowControls = editor && <>
    <ToolButton label="+ Row" ariaLabel="Add row" accent
      disabled={!editor.can().insertScriptRowAfter()}
      onClick={() => editor.chain().focus().insertScriptRowAfter().scrollIntoView().run()} />
    <ToolButton label="× Row" ariaLabel="Delete current row"
      disabled={!editor.can().deleteCurrentScriptRow()}
      onClick={() => editor.chain().focus().deleteCurrentScriptRow().scrollIntoView().run()} />
    {rowButton('duplicate')}{rowButton('up')}{rowButton('down')}
  </>;
  const historyControls = editor && <>
    <ToolButton label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().scrollIntoView().run()} />
    <ToolButton label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().scrollIntoView().run()} />
  </>;
  const headings = editor && <>
    <ToolButton label="H1" ariaLabel="Heading 1" active={editor.isActive('heading', { level: 1 })}
      onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).scrollIntoView().run()} />
    <ToolButton label="H2" ariaLabel="Heading 2" active={editor.isActive('heading', { level: 2 })}
      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).scrollIntoView().run()} />
    <ToolButton label="¶" ariaLabel="Paragraph" active={editor.isActive('paragraph')}
      onClick={() => editor.chain().focus().setParagraph().scrollIntoView().run()} />
  </>;
  const formatting = editor && <>
    <ToolButton label="B" ariaLabel="Bold" bold active={editor.isActive('bold')}
      onClick={() => editor.chain().focus().toggleBold().scrollIntoView().run()} />
    <ToolButton label="I" ariaLabel="Italic" italic active={editor.isActive('italic')}
      onClick={() => editor.chain().focus().toggleItalic().scrollIntoView().run()} />
    <ToolButton label={desktop ? "• List" : "List"} ariaLabel="Bullet list" active={editor.isActive('bulletList')}
      onClick={() => editor.chain().focus().toggleBulletList().scrollIntoView().run()} />
  </>;
  const alignments = editor && <>
    <AlignButton editor={editor} value="left" />
    <AlignButton editor={editor} value="center" />
    <AlignButton editor={editor} value="right" />
  </>;

  return <div ref={anchorRef} className="editor-dock-anchor" data-print-hide>
    {editor && <div ref={dockRef} className={`editor-dock ${desktop ? `editor-dock-desktop${compact ? ' editor-dock-compact' : ''}` : 'editor-dock-phone'}`}
      role="toolbar" aria-label="Script editing tools" style={position}>
      {desktop ? <>
        <div className="editor-dock-group">{headings}</div><Divider />
        <div className="editor-dock-group">{formatting}</div><Divider />
        <div className="editor-dock-group">{alignments}</div><Divider />
        <div className="editor-dock-group">{rowControls}</div><Divider />
        <div className="editor-dock-group"><RefImageButton editor={editor} iconOnly={compact} /></div><Divider />
        <SectionMenu editor={editor} /><Divider />
        <div className="editor-dock-group">{historyControls}</div>
      </> : <>
        {rowControls}{historyControls}{headings}{formatting}
        <div ref={moreRef} className="editor-dock-more-wrap">
          <button type="button" className="editor-dock-more-button" aria-label="More editing tools" aria-expanded={expanded}
            onMouseDown={event => event.preventDefault()} onClick={() => setExpanded(value => !value)}>More</button>
          {expanded && <div className="editor-dock-more" role="group" aria-label="More editing tools">
            <div className="editor-dock-group">{alignments}<RefImageButton editor={editor} /><SectionMenu editor={editor} /></div>
          </div>}
        </div>
      </>}
    </div>}
  </div>;
}

interface ButtonProps {
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  bold?: boolean;
  italic?: boolean;
  accent?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  children?: React.ReactNode;
}

function ToolButton({
  label,
  onClick,
  active,
  disabled,
  bold,
  italic,
  accent,
  ariaLabel,
  ariaExpanded,
  children,
}: ButtonProps) {
  let stateClass = '';
  if (active) {
    stateClass = 'bg-accent text-accent-fg';
  } else if (accent) {
    stateClass = 'text-accent hover:bg-accent/10';
  } else {
    stateClass = 'text-muted hover:text-fg hover:bg-elevated';
  }
  const fontClass = `${bold ? 'font-bold' : ''} ${italic ? 'italic' : ''}`.trim();

  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      aria-pressed={active}
      aria-expanded={ariaExpanded}
      className={`
        px-2.5 py-1 rounded-sm
        font-mono text-[11px] uppercase tracking-[0.08em] font-medium
        transition-colors duration-150
        disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed
        ${stateClass} ${fontClass}
      `}
    >
      {children ?? label}
    </button>
  );
}

function AlignButton({
  editor,
  value,
}: {
  editor: Editor;
  value: 'left' | 'center' | 'right';
}) {
  const active = editor.isActive({ textAlign: value });
  return (
    <ToolButton
      label={value === 'left' ? 'L' : value === 'center' ? 'C' : 'R'}
      ariaLabel={`Align ${value}`}
      active={active}
      onClick={() => editor.chain().focus().setTextAlign(value).scrollIntoView().run()}
    >
      <AlignIcon value={value} />
    </ToolButton>
  );
}

function AlignIcon({ value }: { value: 'left' | 'center' | 'right' }) {
  const lines =
    value === 'left'
      ? [
          [3, 6, 13, 6],
          [3, 10, 17, 10],
          [3, 14, 11, 14],
        ]
      : value === 'center'
      ? [
          [5, 6, 15, 6],
          [3, 10, 17, 10],
          [6, 14, 14, 14],
        ]
      : [
          [7, 6, 17, 6],
          [3, 10, 17, 10],
          [9, 14, 17, 14],
        ];
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden
    >
      {lines.map(([x1, y1, x2, y2], i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
      ))}
    </svg>
  );
}

function SectionMenu({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function insertScriptSection() {
    editor
      .chain()
      .focus('end')
      .insertContent([
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'New section' }] },
        { type: 'scriptHeaderRow' },
        scriptRowJSON('', ''),
        scriptRowJSON('', ''),
        scriptRowJSON('', ''),
      ])
      .scrollIntoView().run();
    setOpen(false);
  }

  function insertInterviewSection() {
    editor
      .chain()
      .focus('end')
      .insertContent([
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Interview questions' }],
        },
        {
          type: 'paragraph',
          content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Subject: ' }, { type: 'text', text: '[Interviewee name + role]' }],
        },
        {
          type: 'bulletList',
          content: [
            bulletItem('Tell us about yourself and what you do.'),
            bulletItem('What was the situation before working with us?'),
            bulletItem('What changed once we started?'),
            bulletItem('What does it look like now?'),
            bulletItem('What would you say to someone considering us?'),
          ],
        },
      ])
      .scrollIntoView().run();
    setOpen(false);
  }

  function insertNotesSection() {
    editor
      .chain()
      .focus('end')
      .insertContent([
        {
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: 'Production notes' }],
        },
        { type: 'paragraph' },
      ])
      .scrollIntoView().run();
    setOpen(false);
  }

  function findCurrentSection(): { from: number; to: number; title: string } | null {
    const cursor = editor.state.selection.from;
    let sectionStart = -1;
    let sectionTitle = '';
    let sectionEnd = -1;

    editor.state.doc.forEach((child, offset) => {
      if (child.type.name === 'heading' && (child.attrs as { level: number }).level === 2) {
        if (offset <= cursor) {
          sectionStart = offset;
          sectionTitle = child.textContent;
        } else if (sectionEnd === -1) {
          sectionEnd = offset;
        }
      }
    });

    if (sectionStart === -1) return null;
    if (sectionEnd === -1) sectionEnd = editor.state.doc.content.size;
    return { from: sectionStart, to: sectionEnd, title: sectionTitle };
  }

  const removable = findCurrentSection();

  function removeCurrentSection() {
    if (!removable) return;
    const ok = window.confirm(
      `Remove "${removable.title || 'this section'}" from the script?\nUse Cmd/Ctrl+Z to undo.`,
    );
    if (!ok) return;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        tr.delete(removable.from, removable.to);
        return true;
      })
      .scrollIntoView().run();
    setOpen(false);
  }

  // Find every scriptRow within the current section. Used by Clear rows to
  // wipe just the audio/visual pairs while keeping the H2 + header row.
  function findRowsInCurrentSection(): Array<{ from: number; to: number }> {
    if (!removable) return [];
    const ranges: Array<{ from: number; to: number }> = [];
    editor.state.doc.forEach((child, offset) => {
      if (
        offset > removable.from &&
        offset < removable.to &&
        child.type.name === 'scriptRow'
      ) {
        ranges.push({ from: offset, to: offset + child.nodeSize });
      }
    });
    return ranges;
  }

  function clearRowsInSection() {
    const ranges = findRowsInCurrentSection();
    if (ranges.length === 0) {
      setOpen(false);
      return;
    }
    const ok = window.confirm(
      `Delete all ${ranges.length} row${ranges.length === 1 ? '' : 's'} in "${
        removable?.title || 'this section'
      }"?\nThe section heading and AUDIO/VISUAL header will stay. Use Cmd/Ctrl+Z to undo.`,
    );
    if (!ok) return;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        // Delete in reverse so positions don't shift mid-iteration.
        for (let i = ranges.length - 1; i >= 0; i--) {
          tr.delete(ranges[i]!.from, ranges[i]!.to);
        }
        return true;
      })
      .scrollIntoView().run();
    setOpen(false);
  }

  const rowCountInSection = removable ? findRowsInCurrentSection().length : 0;

  return (
    <div ref={ref} className="editor-section-menu relative">
      <ToolButton
        label="Section ▾"
        ariaLabel="Section"
        ariaExpanded={open}
        accent
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div className="
          editor-section-popup absolute right-0 bottom-[calc(100%+8px)]
          min-w-[240px] py-1.5
          bg-card border border-line rounded-sm
          shadow-flare-lg/30
          z-30 animate-rise-subtle
        ">
          <SectionOption
            title="Script section"
            hint="Heading + audio/visual rows"
            onClick={insertScriptSection}
          />
          <SectionOption
            title="Interview questions"
            hint="Subject + question list"
            onClick={insertInterviewSection}
          />
          <SectionOption
            title="Production notes"
            hint="Free-form notes block"
            onClick={insertNotesSection}
          />
          {removable && (
            <>
              <div className="h-px bg-line my-1" />
              {rowCountInSection > 0 && (
                <SectionOption
                  title={`Clear ${rowCountInSection} row${rowCountInSection === 1 ? '' : 's'} in this section`}
                  hint="Keeps heading + AUDIO/VISUAL header"
                  destructive
                  onClick={clearRowsInSection}
                />
              )}
              <SectionOption
                title={`Remove "${removable.title || 'current section'}"`}
                hint="Deletes from this heading to the next"
                destructive
                onClick={removeCurrentSection}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SectionOption({
  title,
  hint,
  onClick,
  destructive,
}: {
  title: string;
  hint: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`w-full text-left px-3 py-2 hover:bg-elevated transition-colors duration-150 ${
        destructive ? 'text-accent' : ''
      }`}
    >
      <div className={`text-[13px] font-medium ${destructive ? 'text-accent' : 'text-fg'}`}>
        {title}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-faint mt-0.5">
        {hint}
      </div>
    </button>
  );
}

function Divider() {
  return <span className="editor-dock-divider" aria-hidden="true" />;
}

function bulletItem(text: string) {
  return {
    type: 'listItem',
    content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
  };
}

function scriptRowJSON(audio: string, visuals: string) {
  return {
    type: 'scriptRow',
    content: [
      {
        type: 'scriptCell',
        // New rows default to Voiceover and B-roll (see shared type helpers).
        attrs: { kind: 'audio', audioType: 'voiceover' },
        content: audio
          ? [{ type: 'paragraph', content: [{ type: 'text', text: audio }] }]
          : [{ type: 'paragraph' }],
      },
      {
        type: 'scriptCell',
        attrs: { kind: 'visuals', visualType: 'broll' },
        content: visuals
          ? [{ type: 'paragraph', content: [{ type: 'text', text: visuals }] }]
          : [{ type: 'paragraph' }],
      },
    ],
  };
}
